'use client';

import React from 'react';
import Image from 'next/image';

import { Button } from '@simulmedia/ui-kit';
import { urlFor } from '@skybeam/shared-sanity';
import { cn } from '@skybeam/shared-utils/classnames/cn';
import { utils } from '../../../_components/utils';
import type {
  ResponsiveValue,
  SanityImage,
} from '../../../_components/utils/types';
import { SignupLinkWithUTM } from '../../../_components/sign-up-link-with-UTM';

export type Badge = {
  hideOnDesktop?: boolean;
  hideOnLaptop?: boolean;
  hideOnMobile?: boolean;
  hideOnTablet?: boolean;
  positionBottom?: ResponsiveValue;
  positionLeft?: ResponsiveValue;
  positionRight?: ResponsiveValue;
  positionTop?: ResponsiveValue;
};

export type BadgeLogo = Badge & {
  badgeType: 'logo';
  image?: SanityImage;
  imageHeight?: ResponsiveValue;
  imageWidth?: ResponsiveValue;
  wrapperBackgroundColor?: string;
  wrapperHeight?: ResponsiveValue;
  wrapperWidth?: ResponsiveValue;
};

export type BadgeText = Badge & {
  badgeType: 'text';
  label?: string;
  textBackgroundColor?: string;
  textBorderColor?: string;
  textColor?: string;
};

export type BadgeIllustration = Badge & {
  badgeType: 'illustration';
  illustrationHeight?: ResponsiveValue;
  illustrationWidth?: ResponsiveValue;
  image?: SanityImage;
};

export type ProductHeadingBadge = BadgeLogo | BadgeText | BadgeIllustration;

function getBadgeVisibilityClasses(
  badge: Badge,
  display: 'block' | 'flex' | 'inline',
): string {
  const d = display;
  return [
    badge.hideOnMobile ? 'hidden' : d,
    `tablet:${badge.hideOnTablet ? 'hidden' : d}`,
    `laptop:${badge.hideOnLaptop ? 'hidden' : d}`,
    `desktop:${badge.hideOnDesktop ? 'hidden' : d}`,
  ].join(' ');
}

export type ProductHeadingSectionProps = {
  badges?: ProductHeadingBadge[];
  bottomImage?: SanityImage;
  buttonLink: {
    blank?: boolean;
    href: string;
    nofollow?: boolean;
  };
  buttonText: string;
  contentMaxWidth?: ResponsiveValue;
  title: string;
  titleHighlight?: string;
  titleHighlightColor?: string;
};

export const ProductHeadingSection = ({
  badges,
  bottomImage,
  buttonLink,
  buttonText,
  contentMaxWidth,
  title,
  titleHighlight,
  titleHighlightColor,
}: ProductHeadingSectionProps) => {
  const contentMaxWidthStyle = utils.responsiveUtils.getResponsiveValueStyleTag(
    contentMaxWidth,
    'max-width',
  );
  const styleTags = [contentMaxWidthStyle.styleTag].filter(
    (tag): tag is React.ReactElement => tag !== null,
  );

  return (
    <>
      {styleTags}
      <div className="tablet:py-48 desktop:py-56 laptop:py-60 relative flex w-full justify-center overflow-hidden bg-[url('/grid-bg.svg')] bg-bottom px-3 pt-56 pb-52">
        <div
          className={cn(
            'relative flex w-full flex-col items-center gap-y-12 text-center',
            contentMaxWidthStyle.className,
          )}
        >
          <h1 className="heading-marketing-1 tablet:px-6 laptop:px-0 z-10 font-light">
            {title}
            {titleHighlight && (
              <span
                {...utils.highlightSpanProps.getHighlightSpanProps(
                  titleHighlightColor,
                )}
              >
                {' '}
                {titleHighlight}
              </span>
            )}
          </h1>
          <SignupLinkWithUTM href={buttonLink?.href} rel="noopener noreferrer">
            <Button
              data-test="get-started-bottom-btn"
              className="tablet:h-12 tablet:px-8 tablet:py-2.5 tablet:text-lg! laptop:h-14 laptop:px-10 laptop:py-3 laptop:text-2xl! px-6 py-2 text-[1rem]! leading-normal! font-semibold! text-white!"
              variant="primary"
            >
              {buttonText}
            </Button>
          </SignupLinkWithUTM>
          {badges?.map((badge, index) => {
            const type = badge.badgeType;
            if (!type) return null;

            const topStyle = badge.positionTop
              ? utils.responsiveUtils.getResponsiveValueStyleTag(
                  badge.positionTop,
                  'top',
                )
              : null;
            const leftStyle = badge.positionLeft
              ? utils.responsiveUtils.getResponsiveValueStyleTag(
                  badge.positionLeft,
                  'left',
                )
              : null;
            const rightStyle = badge.positionRight
              ? utils.responsiveUtils.getResponsiveValueStyleTag(
                  badge.positionRight,
                  'right',
                )
              : null;
            const bottomStyle = badge.positionBottom
              ? utils.responsiveUtils.getResponsiveValueStyleTag(
                  badge.positionBottom,
                  'bottom',
                )
              : null;
            const positionClassNames = cn(
              topStyle?.className,
              leftStyle?.className,
              rightStyle?.className,
              bottomStyle?.className,
            );
            const visibilityClass = getBadgeVisibilityClasses(badge, 'flex');

            if (type === 'logo') {
              const b = badge as BadgeLogo;
              const imageUrl =
                b.image?.asset && urlFor(b.image.asset).width(200).url();
              if (!imageUrl) return null;
              const wrapperW = b.wrapperWidth
                ? utils.responsiveUtils.getResponsiveValueStyleTag(
                    b.wrapperWidth,
                    'width',
                  )
                : null;
              const wrapperH = b.wrapperHeight
                ? utils.responsiveUtils.getResponsiveValueStyleTag(
                    b.wrapperHeight,
                    'height',
                  )
                : null;
              const imgW = b.imageWidth
                ? utils.responsiveUtils.getResponsiveValueStyleTag(
                    b.imageWidth,
                    'max-width',
                  )
                : null;
              const imgH = b.imageHeight
                ? utils.responsiveUtils.getResponsiveValueStyleTag(
                    b.imageHeight,
                    'max-height',
                  )
                : null;
              const wrapperBg = b.wrapperBackgroundColor?.trim();
              const wrapperStyle: React.CSSProperties = wrapperBg
                ? { backgroundColor: wrapperBg }
                : {};
              return (
                <React.Fragment key={index}>
                  {topStyle?.styleTag}
                  {leftStyle?.styleTag}
                  {rightStyle?.styleTag}
                  {bottomStyle?.styleTag}
                  {wrapperW?.styleTag}
                  {wrapperH?.styleTag}
                  {imgW?.styleTag}
                  {imgH?.styleTag}
                  <div
                    className={cn(
                      'absolute z-1 flex items-center justify-center rounded-[10px] shadow-lg',
                      positionClassNames,
                      visibilityClass,
                      !wrapperW?.className && !wrapperH?.className
                        ? 'size-20'
                        : '',
                      wrapperW?.className,
                      wrapperH?.className,
                    )}
                    style={
                      Object.keys(wrapperStyle).length > 0
                        ? wrapperStyle
                        : undefined
                    }
                  >
                    <Image
                      className={cn(
                        'object-contain',
                        imgW?.className ?? 'max-w-[83px]',
                        imgH?.className,
                      )}
                      src={imageUrl}
                      alt={b.image?.alt ?? ''}
                      width={120}
                      height={60}
                    />
                  </div>
                </React.Fragment>
              );
            }

            if (type === 'text') {
              const b = badge as BadgeText;
              if (!b.label) return null;
              const textVisibilityClass = getBadgeVisibilityClasses(
                badge,
                'inline',
              );
              const textStyle: React.CSSProperties = {
                ...(b.textBackgroundColor && {
                  backgroundColor: b.textBackgroundColor,
                }),
                ...(b.textBorderColor && {
                  borderColor: b.textBorderColor,
                }),
                ...(b.textColor && { color: b.textColor }),
              };
              const hasCustomColors =
                b.textBackgroundColor || b.textBorderColor || b.textColor;
              return (
                <React.Fragment key={index}>
                  {topStyle?.styleTag}
                  {leftStyle?.styleTag}
                  {rightStyle?.styleTag}
                  {bottomStyle?.styleTag}
                  <span
                    className={cn(
                      'tablet:text-[15px] laptop:px-5 laptop:text-xl absolute z-1 rounded-[10px] border-[1.5px] px-1.5 pt-1.5 pb-1 text-xs leading-[1.3] font-semibold',
                      positionClassNames,
                      textVisibilityClass,
                      !hasCustomColors &&
                        'border-blue-100 bg-blue-50 text-blue-600',
                    )}
                    style={
                      Object.keys(textStyle).length > 0 ? textStyle : undefined
                    }
                  >
                    {b.label}
                  </span>
                </React.Fragment>
              );
            }

            if (type === 'illustration') {
              const b = badge as BadgeIllustration;
              const imageUrl = b.image?.asset && urlFor(b.image.asset).url();
              if (!imageUrl) return null;
              const illustrationVisibilityClass = getBadgeVisibilityClasses(
                badge,
                'block',
              );
              const illW = b.illustrationWidth
                ? utils.responsiveUtils.getResponsiveValueStyleTag(
                    b.illustrationWidth,
                    'width',
                  )
                : null;
              const illH = b.illustrationHeight
                ? utils.responsiveUtils.getResponsiveValueStyleTag(
                    b.illustrationHeight,
                    'height',
                  )
                : null;
              return (
                <React.Fragment key={index}>
                  {topStyle?.styleTag}
                  {leftStyle?.styleTag}
                  {rightStyle?.styleTag}
                  {bottomStyle?.styleTag}
                  {illW?.styleTag}
                  {illH?.styleTag}
                  <Image
                    className={cn(
                      'absolute z-1 object-contain',
                      positionClassNames,
                      illustrationVisibilityClass,
                      illW?.className,
                      illH?.className,
                    )}
                    src={imageUrl}
                    alt={b.image?.alt ?? ''}
                    width={100}
                    height={100}
                  />
                </React.Fragment>
              );
            }

            return null;
          })}
        </div>
        {bottomImage?.asset && (
          <Image
            className="tablet:min-h-[215px] tablet:object-center absolute bottom-0 z-0 min-h-[161px] object-cover object-[61%_0%]"
            src={urlFor(bottomImage.asset).url()}
            alt={bottomImage.alt ?? 'Illustration'}
            width={2240}
            height={215}
          />
        )}
      </div>
    </>
  );
};
